import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionLabel } from "@/components/site/bits";
import { api } from "@/lib/api";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!items.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const t = items[idx];
  const next = () => setIdx((i) => (i + 1) % items.length);
  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);

  return (
    <section id="testimonials" className="relative py-28 px-6" data-testid="testimonials">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <SectionLabel>Client Success Stories</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            Trusted by <span className="text-gradient">growing teams</span>
          </h2>
        </Reveal>

        <div className="relative mt-12 rounded-3xl glass p-8 md:p-14 overflow-hidden" data-testid="testimonial-card">
          <Quote className="absolute -top-2 left-6 h-24 w-24 text-white/[0.03]" />
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-[#00FFB2] text-[#00FFB2]" />
                ))}
              </div>
              <p className="font-display text-xl md:text-2xl leading-relaxed text-white/90">"{t.review}"</p>
              <div className="mt-8 flex items-center gap-4">
                <img src={t.avatar} alt={t.name} className="h-12 w-12 rounded-full object-cover border border-white/20" />
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-sm text-white/50">{t.role} · {t.company}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-between">
            <div className="flex gap-2">
              {items.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#00E5FF]" : "w-2 bg-white/20"}`} data-testid={`testimonial-dot-${i}`} />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prev} className="grid h-10 w-10 place-items-center rounded-full glass hover:border-[#00E5FF]/50" data-testid="testimonial-prev"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={next} className="grid h-10 w-10 place-items-center rounded-full glass hover:border-[#00E5FF]/50" data-testid="testimonial-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
